import Config from '@/config'
import { EXPORT_TYPE_TO_BACKGROUND_TASK_KIND } from '@/constant/backgroundTasks.constant'

export const SIMULATE_EXPORT_PARAM = 'simulateExport'
export const SIMULATE_EXPORT_FAIL_PARAM = 'simulateExportFail'
export const SIMULATED_EXPORT_FILE_NAME = 'Simulated export'
export const SIMULATED_DOWNLOAD_URL = 'data:text/plain;charset=utf-8,Simulated%20export'

const PERCENT_STEP = 10
const PERCENT_INTERVAL_MS = 500
const NO_PERCENTAGE_DURATION_MS = 6000

// Allowlist, so an env added later stays off until someone opts it in.
const SIMULATION_ALLOWED_ENVS = new Set(['local', 'develop'])

const MODULE = 'backgroundTasks'
const PERCENTAGE_KINDS = new Set(['pdf', 'image'])

const parseKinds = (value) => (value || '')
  .split(',')
  .map((kind) => kind.trim().toLowerCase())
  .filter((kind, index, kinds) => EXPORT_TYPE_TO_BACKGROUND_TASK_KIND[kind] && kinds.indexOf(kind) === index)

/**
 * The simulation requested by `?simulateExport=pdf,image,excel` (any subset) and
 * `?simulateExportFail=<kinds>` (those kinds end failed), or null when nothing
 * should run (not an allowed env, or no valid kind).
 */
export const readSimulatedExport = (search = window.location.search) => {
  if (!SIMULATION_ALLOWED_ENVS.has(Config.env)) return null
  const params = new URLSearchParams(search)
  const kinds = parseKinds(params.get(SIMULATE_EXPORT_PARAM))
  if (!kinds.length) return null
  return { kinds, failKinds: parseKinds(params.get(SIMULATE_EXPORT_FAIL_PARAM)) }
}

/**
 * Plays fake export runs through the real backgroundTasks store so the cards
 * render without a real export. Pdf and image report a percentage; excel reports
 * none, like the real job. Try again replays the same run. Returns a function
 * that stops every run.
 */
export const startSimulatedExport = (store, { kinds, failKinds = [], fileName = SIMULATED_EXPORT_FILE_NAME }) => {
  const timers = new Set()
  let stopped = false

  const later = (callback, delayMs) => {
    const timer = setTimeout(() => {
      timers.delete(timer)
      if (!stopped) callback()
    }, delayMs)
    timers.add(timer)
  }

  const finish = (id, shouldFail) => {
    if (shouldFail) store.dispatch(`${MODULE}/failBackgroundTaskAction`, { id })
    else store.dispatch(`${MODULE}/completeBackgroundTaskAction`, { id, downloadUrl: SIMULATED_DOWNLOAD_URL })
  }

  const playPercentage = (id, shouldFail, percent) => {
    const failAt = 50
    if (shouldFail && percent >= failAt) return finish(id, true)
    if (percent >= 100) return finish(id, false)
    store.dispatch(`${MODULE}/updateBackgroundTaskProgressAction`, { id, value: percent })
    later(() => playPercentage(id, shouldFail, percent + PERCENT_STEP), PERCENT_INTERVAL_MS)
    return undefined
  }

  const play = (kind) => {
    const id = EXPORT_TYPE_TO_BACKGROUND_TASK_KIND[kind]
    const shouldFail = failKinds.includes(kind)
    store.dispatch(`${MODULE}/startBackgroundTaskAction`, {
      id,
      fileName,
      retry: () => play(kind),
    })
    if (PERCENTAGE_KINDS.has(kind)) {
      later(() => playPercentage(id, shouldFail, PERCENT_STEP), PERCENT_INTERVAL_MS)
    } else {
      later(() => finish(id, shouldFail), NO_PERCENTAGE_DURATION_MS)
    }
  }

  kinds.forEach(play)

  return () => {
    stopped = true
    timers.forEach(clearTimeout)
    timers.clear()
  }
}
